import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import TypeTemplateList from 'helpers/typeTemplateList'
import type { GetPrintSTOResponseType, PrintStoItemType } from 'utils/apiList/stockOpname'

export interface DataPrintBarcodeStateType {
  expiryDate: number | null
  barcode: string | number
  productName: string
  printQRQTY: number
}

type StoItemType = PrintStoItemType & { uniqueId: string }

type PrintStoDataType = Omit<GetPrintSTOResponseType['data'], 'stockOpnameItems'> & {
  stockOpnameItems: StoItemType[]
}

type ModifierVariantsType = {
  id: number
  name: string
  modifier_variants: {
    id: number
    change_type: string
    name: string
  }[]
}

type PrintAstroKitchenLabelType = Nullable<
  {
    customer: string
    customerNote: string
    productName: string
    itemId: number
    orderId: string
    modifiers: ModifierVariantsType[]
    addOns: ModifierVariantsType[]
  }[]
>
interface PrintAreaStateType {
  isLoading: boolean
  typeTobePrinted: string
  dataPrintBarcode: DataPrintBarcodeStateType
  printSTO: PrintStoDataType
  astroKitchenLabel: PrintAstroKitchenLabelType
  generatePdfPoNumber: string
  pdfBlobData: {
    poNumber: string
    blobUrl: string
  }
}

interface DataPrintStockOpnameType {
  doc: 'BERITA_ACARA' | 'STO'
  data: PrintStoDataType
}

export const initialState: PrintAreaStateType = {
  isLoading: false,
  typeTobePrinted: '',
  dataPrintBarcode: { barcode: '', expiryDate: 0, printQRQTY: 0, productName: '' },
  printSTO: {
    id: 0,
    stockOpnameNumber: '',
    createdAt: 0,
    locationId: 0,
    locationName: '',
    assignName: '',
    stockOpnameItems: [],
    totalSkuRack: 0,
    typeReference: '',
  },
  astroKitchenLabel: null,
  generatePdfPoNumber: '',
  pdfBlobData: {
    poNumber: '',
    blobUrl: '',
  },
}

const printArea = createSlice({
  name: 'printArea',
  initialState,
  reducers: {
    reset: () => initialState,
    setTypeTobePrinted: (state, action: PayloadAction<string>) => {
      state.typeTobePrinted = action.payload
    },
    setDataPrintBarcode: (state, action: PayloadAction<DataPrintBarcodeStateType>) => {
      state.dataPrintBarcode = action.payload
      state.typeTobePrinted = TypeTemplateList.FPOPrintBarcode
      setTimeout(() => {
        window.print()
      })
    },
    setDataPrintStockOpname: (state, action: PayloadAction<DataPrintStockOpnameType>) => {
      state.typeTobePrinted =
        action.payload.doc === 'BERITA_ACARA'
          ? TypeTemplateList.StockOpnameBeritaAcara
          : TypeTemplateList.StockOpname

      if (action.payload.doc === 'STO') {
        const { data } = action.payload
        state.printSTO = data
      }

      setTimeout(() => {
        window.print()
      })
    },
    setDataKitchenModifierReceipt: (state, action: PayloadAction<PrintAstroKitchenLabelType>) => {
      state.astroKitchenLabel = action.payload
      state.typeTobePrinted = TypeTemplateList.KitchenModifierReceipt
      setTimeout(() => {
        window.print()
      })
    },
    setGeneratePdfPoNumber: (state, action: PayloadAction<string>) => {
      state.generatePdfPoNumber = action.payload
    },
    setPdfBlobData: (state, action: PayloadAction<PrintAreaStateType['pdfBlobData']>) => {
      state.pdfBlobData = action.payload
    },
  },
})

export const {
  reset,
  setTypeTobePrinted,
  setDataPrintBarcode,
  setDataPrintStockOpname,
  setDataKitchenModifierReceipt,
  setGeneratePdfPoNumber,
  setPdfBlobData,
} = printArea.actions
export default printArea.reducer
